/**
 * @fileoverview 应用入口文件
 * @description 初始化 React 应用，配置全局 Provider
 *
 * 关键设计：
 * - TG Mini App 环境：不加载 WagmiProvider（避免 wagmi 初始化时触发 crypto.subtle 崩溃）
 * - Web 环境：动态 import wagmi，懒加载 WagmiProvider
 * - TonConnectUIProvider 已移至 Wallet 页面内部
 * @module main
 */

import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import App from './App';
import './index.css';
// 初始化国际化配置（必须在 App 之前引入）
import './locales/i18n';

// 检测是否在 TG Mini App 环境（不依赖 window.Telegram，只看 URL hash 或 platform）
// 在模块加载阶段用最简单的方式检测，避免复杂逻辑
function isTelegramEnv(): boolean {
  try {
    const tg = (window as any).Telegram?.WebApp;
    if (!tg) return false;
    const platform = tg.platform;
    // platform 为 'unknown' 或空时是普通浏览器
    return typeof platform === 'string' && platform !== '' && platform !== 'unknown';
  } catch {
    return false;
  }
}

// 创建 React Query 客户端
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

async function bootstrap() {
  const root = createRoot(document.getElementById('root')!);

  if (isTelegramEnv()) {
    // TG 环境：不加载 wagmi，直接渲染 App
    root.render(
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    );
  } else {
    // Web 环境：动态 import wagmi（只有非 TG 环境才加载）
    const [{ WagmiProvider }, { wagmiConfig }] = await Promise.all([
      import('wagmi'),
      import('./lib/wagmiConfig'),
    ]);

    root.render(
      // reconnectOnMount={false}：禁用 wagmi 挂载时自动重连注入钱包。
      // 自动重连会持有一个挂起的 eth_requestAccounts，与登录时的直连请求并发 → 手机 MetaMask 内置浏览器报 -32002。
      // 登录态在 useAppStore（后端 token），不依赖 wagmi 连接状态，故禁用重连无副作用。
      <WagmiProvider config={wagmiConfig} reconnectOnMount={false}>
        <QueryClientProvider client={queryClient}>
          <App />
        </QueryClientProvider>
      </WagmiProvider>
    );
  }
}

bootstrap();
